import * as React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import AccountPage from '../pages/AccountPage';
import ApplicationPage from '../pages/ApplicationPage';
import LegacyChangePasswordBackstopPage from '../pages/LegacyChangePasswordBackstopPage';
import LoginCallbackPage from '../pages/LoginCallbackPage';
import LoginPage from '../pages/LoginPage';
import PresentationPage from '../pages/PresentationPage';
import UnsubscribePage from '../pages/UnsubscribePage';
import HomePage from '../pages/HomePage';
import EnterpriseWelcomePage from '../pages/EnterpriseWelcomePage';
import ActivatePage from '../pages/ActivatePage';
import * as paths from './paths';
import useCurrentUserRoles from '../hooks/useCurrentUserRoles';
import { useSelector } from 'react-redux';
import {
  selectIsEnterpriseUser,
  selectIsLiteUser,
  selectUserIsFetching,
} from '../selectors/user';
import IntegrationPage from '../pages/IntegrationPage';
import MarketplacePage from '../pages/MarketplacePage';
import AnalyticsPageV2 from '../pages/AnalyticsPage/ProductEngagementPage';
import ContentEngagementPage from '../pages/AnalyticsPage/ContentEngagementPage';
import TrafficAwarenessPage from '../pages/AnalyticsPage/TrafficAwarenessPage';
import ContentDetailPage from '../pages/AnalyticsPage/ContentDetailPage';
import ProductHubCatalogPage from '../pages/ProductHubPage/ProductHubCatalogPage';
import ProductHubIntegrationsPage from '../pages/ProductHubPage/ProductHubIntegrationsPage';
import LocationsPage from '../pages/LocationsPage';
import ProductHubCreateIntegrationPage from '../pages/ProductHubPage/ProductHubCreateIntegrationPage';
import ProductHubEditIntegrationRouter from '../pages/ProductHubPage/ProductHubEditIntegrationRouter';
import ProductHubCreateCatalogPage from '../pages/ProductHubPage/ProductHubCreateCatalogPage';
import ProductHubCatalogDetailsPage from '../pages/ProductHubPage/ProductHubCatalogDetailsPage';
import LoadingModal from '@raydiant/raydial/components/LoadingModal';
import ProductHubEditCataloguePage from '../pages/ProductHubPage/ProductHubEditCataloguePage';
import LibraryPageV2 from '../pages/LibraryPage/v2/LibraryPageV2';
import AddContentPageV2 from '../pages/AddContentPage/AddContentPageV2';
import ApplicationsPageV2 from '../pages/ApplicationsPage/ApplicationsPageV2';
import GettingStartedPage from '../pages/GettingStartedPage';
import PlansPage from '../pages/PlansPage';
import PlaylistPageV2 from '../pages/PlaylistPage/v2/PlaylistPageV2';

const ProductHubRoutes = () => {
  const isUserFetching = useSelector(selectUserIsFetching);
  const roles = useCurrentUserRoles();

  if (isUserFetching) {
    return <LoadingModal isOpen={true} />;
  }

  if (!roles.isProductHubEnabled) {
    return null;
  }

  return (
    <Switch>
      <Route
        exact
        path={paths.integrations.pattern}
        component={ProductHubIntegrationsPage}
      />
      <Route
        exact
        path={paths.createCatalog.pattern}
        component={ProductHubCreateCatalogPage}
      />
      <Route
        exact
        path={paths.createIntegrations.pattern}
        component={ProductHubCreateIntegrationPage}
      />
      <Route
        exact
        path={paths.editIntegrations.pattern}
        component={ProductHubEditIntegrationRouter}
      />
      <Route
        exact
        path={paths.catalogDetails.pattern}
        component={ProductHubCatalogDetailsPage}
      />
      <Route
        exact
        path={paths.catalogs.pattern}
        component={ProductHubCatalogPage}
      />
      <Route
        exact
        path={paths.editCatalog.pattern}
        component={ProductHubEditCataloguePage}
      />
      <Route
        exact
        path={paths.catalogs.pattern}
        component={ProductHubCatalogPage}
      />
    </Switch>
  );
};

const Routes: React.SFC = () => {
  const roles = useCurrentUserRoles();
  const isEnterpriseUser = useSelector(selectIsEnterpriseUser);
  const isLiteUser = useSelector(selectIsLiteUser);

  return (
    <Switch>
      <Route path={paths.loginCallback.pattern} component={LoginCallbackPage} />
      <Route path={paths.login.pattern} component={LoginPage} />
      <Route path={paths.activate.pattern} component={ActivatePage} />
      <Route exact path={paths.home.pattern} component={HomePage} />
      <Route path={paths.screens.pattern} component={LocationsPage} />

      <Route
        path={paths.gettingStarted.pattern}
        component={GettingStartedPage}
      />

      <Route path={paths.plans.pattern} component={PlansPage} />

      {/* Redirect legacy /library?scope=shared_library links to the dedicated shared-libraries route */}
      <Route
        exact
        path={paths.library.pattern}
        render={({ location }) => {
          const params = new URLSearchParams(location.search);
          if (params.get('scope') === 'shared_library') {
            // Forward the full query string so params like search/sort_by are not silently dropped
            const search = params.toString();
            const to = `${paths.librarySharedLibraries()}${
              search ? `?${search}` : ''
            }`;
            return <Redirect to={to} />;
          }
          return <LibraryPageV2 />;
        }}
      />
      <Route
        exact
        path={[
          paths.librarySearch.pattern,
          paths.librarySharedWithMe.pattern,
          paths.libraryUsersLibraries.pattern,
        ]}
        component={LibraryPageV2}
      />
      <Route
        exact
        path={paths.librarySharedLibraries.pattern}
        component={LibraryPageV2}
      />

      <Route path={paths.account.pattern} component={AccountPage} />

      <Route
        exact
        path={[
          paths.newPresentation.pattern,
          paths.newPresentationNewTheme.pattern,
          paths.newPresentationEditTheme.pattern,
          paths.newPresentationManageThemes.pattern,
          paths.editPresentation.pattern,
          paths.editPresentationNewTheme.pattern,
          paths.editPresentationEditTheme.pattern,
          paths.editPresentationManageThemes.pattern,
        ]}
        component={PresentationPage}
      />

      <Route path={paths.presentations.pattern} component={AddContentPageV2} />

      <Route path={paths.application.pattern} component={ApplicationPage} />

      <Route path={paths.applications.pattern} component={ApplicationsPageV2} />

      <Route path={paths.unsubscribe.pattern} component={UnsubscribePage} />
      <Route path={paths.newPlaylist.pattern} component={PlaylistPageV2} />
      <Route
        path={paths.editPlaylist.pattern}
        render={({ match }) => <PlaylistPageV2 key={match.params.playlistId} />}
      />
      <Route
        path={paths.enterpriseWelcome.pattern}
        component={EnterpriseWelcomePage}
      />
      <Route
        exact
        path={paths.marketplace.pattern}
        render={() =>
          isLiteUser ? <Redirect to={paths.screens()} /> : <MarketplacePage />
        }
      />
      <Route
        path={paths.trafficAwareness.pattern}
        render={() =>
          isLiteUser ? (
            <Redirect to={paths.screens()} />
          ) : (
            <TrafficAwarenessPage />
          )
        }
      />
      <Route
        exact
        path={paths.contentEngagement.pattern}
        render={() =>
          isLiteUser ? (
            <Redirect to={paths.screens()} />
          ) : (
            <ContentEngagementPage />
          )
        }
      />
      <Route
        exact
        path={paths.contentDetail.pattern}
        render={() =>
          isLiteUser ? <Redirect to={paths.screens()} /> : <ContentDetailPage />
        }
      />
      <Route
        exact
        path={paths.analytics.pattern}
        render={() =>
          isLiteUser ? <Redirect to={paths.screens()} /> : <AnalyticsPageV2 />
        }
      />
      <Route
        path={[paths.integrations.pattern, paths.catalogs.pattern]}
        render={() =>
          isLiteUser ? <Redirect to={paths.screens()} /> : <ProductHubRoutes />
        }
      />

      {roles.isAIPOSEnabled && isEnterpriseUser && (
        <Route path={paths.integration.pattern} component={IntegrationPage} />
      )}
      {/* TODO: Legacy changePassword redirect, remove after enterprise migration is complete. */}
      <Route
        path={paths.legacyChangePassword.pattern}
        component={LegacyChangePasswordBackstopPage}
      />
      {/* Redirects */}
      <Route
        path={paths.indexHtml.pattern}
        render={() => <Redirect to={paths.screens()} />}
      />
      {/* TODO: 404 page? */}
      <Route render={() => <Redirect to={paths.screens()} />} />
    </Switch>
  );
};

export default Routes;
